/**
 * 论文编写 —— 第 0 步：论文信息（论文标题 + 研究领域 + 关键词 + 核心创新点 + 论文模板）
 *
 * 与投标文件的「标书解析」不同：论文不是解析招标文件，而是录入
 * 论文标题、核心创新点与研究领域/关键词，并上传论文模板（进知识库），
 * 这些要素会注入后续目录与正文生成。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  BookOpenIcon,
  CheckCircleIcon,
  TrashIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { projectApi, knowledgeApi, getErrorMessage } from '../services/api';
import type { ProjectInfo, AppMeta, KnowledgeBaseFile } from '../types';

interface PaperSetupProps {
  project: ProjectInfo;
  onSaved: (project: ProjectInfo) => void;
  readOnly?: boolean;
}

const EMPTY_META: AppMeta = {
  app_field: '',
  app_keywords: '',
};

const PaperSetup: React.FC<PaperSetupProps> = ({ project, onSaved, readOnly = false }) => {
  const [name, setName] = useState(project.name || '');
  const [innovationPoints, setInnovationPoints] = useState(project.innovation_points || '');
  const [meta, setMeta] = useState<AppMeta>({ ...EMPTY_META, ...(project.app_meta || {}) });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateFiles, setTemplateFiles] = useState<KnowledgeBaseFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);

  // 论文模板存放的知识库：取项目关联的第一个知识库
  const templateKbId = project.knowledge_base_ids?.[0] ?? null;

  const loadTemplateFiles = useCallback(async (kbId: number | null) => {
    if (!kbId) {
      setTemplateFiles([]);
      return;
    }
    setLoadingFiles(true);
    try {
      const res = await knowledgeApi.listFiles(kbId);
      setTemplateFiles(res.data.files || []);
    } catch {
      setTemplateFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplateFiles(templateKbId);
  }, [templateKbId, loadTemplateFiles]);

  const setMetaField = (key: keyof AppMeta, value: string) => {
    setMeta((prev) => ({ ...prev, [key]: value }));
  };

  // AI 生成核心创新点（基于论文标题）
  const handleAiGenerateInnovation = async () => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: '请先填写论文标题' });
      return;
    }
    setAiGenerating(true);
    setMessage(null);
    setInnovationPoints('');
    try {
      const full = await projectApi.generateInnovationPointsStream(
        project.id,
        name.trim(),
        project.description || undefined,
        (fullText) => setInnovationPoints(fullText),
      );
      setInnovationPoints(full.trim());
      setMessage({ type: 'success', text: '已生成核心创新点，可继续编辑后保存' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, 'AI 生成失败') });
    } finally {
      setAiGenerating(false);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: '请填写论文标题' });
      return;
    }
    try {
      setSaving(true);
      setMessage(null);
      const res = await projectApi.update(project.id, {
        name: name.trim(),
        innovation_points: innovationPoints,
        app_meta: meta,
      });
      onSaved(res.data);
      setMessage({ type: 'success', text: '论文信息已保存' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '保存失败') });
    } finally {
      setSaving(false);
    }
  };

  // 上传论文模板到知识库：作为体例参考参与目录/正文生成（支持多文件）
  const handleTemplateUpload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    try {
      setUploadingTemplate(true);
      setMessage(null);
      let kbId = templateKbId;
      if (!kbId) {
        const kb = await knowledgeApi.create({ name: `【论文模板】${project.name}` });
        kbId = kb.data.id;
        const updated = await projectApi.update(project.id, {
          knowledge_base_ids: [kbId, ...(project.knowledge_base_ids || [])],
        });
        onSaved(updated.data);
      }
      await knowledgeApi.uploadFiles(kbId, list);
      await loadTemplateFiles(kbId);
      setMessage({ type: 'success', text: `已上传 ${list.length} 个论文模板到知识库` });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '论文模板上传失败') });
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleTemplateDelete = async (fileId: number) => {
    if (!templateKbId) return;
    if (!window.confirm('确定删除该论文模板吗？')) return;
    try {
      await knowledgeApi.deleteFile(templateKbId, fileId);
      await loadTemplateFiles(templateKbId);
      setMessage({ type: 'success', text: '模板已删除' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '删除失败') });
    }
  };

  const fieldCls =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 标题 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">📄 论文信息</h2>
        <p className="text-sm text-gray-500">
          填写论文标题、研究领域、关键词与核心创新点，可上传论文模板作为体例参考，这些信息将用于生成目录与正文。
        </p>
      </div>

      {/* 论文基本信息 */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">论文基本信息</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">论文标题</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
            className={fieldCls}
            placeholder="请输入论文标题"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">研究领域</label>
            <input
              type="text"
              value={meta.app_field || ''}
              onChange={(e) => setMetaField('app_field', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="所属领域 / 学科方向"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">关键词</label>
            <input
              type="text"
              value={meta.app_keywords || ''}
              onChange={(e) => setMetaField('app_keywords', e.target.value)}
              disabled={readOnly}
              className={fieldCls}
              placeholder="多个关键词用顿号或逗号分隔"
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">核心创新点</label>
            <button
              type="button"
              onClick={() => void handleAiGenerateInnovation()}
              disabled={readOnly || aiGenerating}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
              title="AI 根据论文标题生成核心创新点"
            >
              <SparklesIcon className="w-3.5 h-3.5 mr-1" />
              {aiGenerating ? '生成中...' : 'AI 生成'}
            </button>
          </div>
          <textarea
            value={innovationPoints}
            onChange={(e) => setInnovationPoints(e.target.value)}
            rows={4}
            disabled={readOnly}
            className={fieldCls}
            placeholder="论文的核心创新点，将用于目录与正文生成"
          />
        </div>
      </div>

      {/* 论文模板 */}
      <div className="bg-white rounded-lg shadow p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-800">论文模板</h3>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-purple-600" />
              <div>
                <div className="text-sm font-medium text-gray-800">论文模板（进知识库）</div>
                <div className="text-xs text-gray-400">
                  {templateFiles.length ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircleIcon className="w-3.5 h-3.5" /> 已上传 {templateFiles.length} 个模板
                    </span>
                  ) : (
                    '上传期刊/学校论文模板，作为体例参考参与目录与正文生成（可上传多个）'
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => templateInputRef.current?.click()}
              disabled={readOnly || uploadingTemplate}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-1" />
              {uploadingTemplate ? '上传中...' : '上传模板'}
            </button>
            <input
              ref={templateInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.xlsx,.xls"
              multiple
              className="hidden"
              disabled={readOnly}
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length) void handleTemplateUpload(files);
                e.target.value = '';
              }}
            />
          </div>

          {loadingFiles ? (
            <div className="mt-3 text-xs text-gray-400">加载模板列表...</div>
          ) : templateFiles.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {templateFiles.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate text-gray-700">{f.original_name}</span>
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => void handleTemplateDelete(f.id)}
                      className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                      title="删除模板"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* 保存 */}
      {!readOnly && (
        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存论文信息'}
          </button>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

export default PaperSetup;
